import { Link } from "react-router-dom";
import { Target, TrendingUp, Shield, Swords, Trophy, Users, Brain, Zap } from "lucide-react";

export default function Strategy() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="text-center mb-10">
        <h1 className="font-cinzel text-4xl md:text-5xl text-amber-300 uppercase tracking-wide mb-3">
          Advanced Strategy Guide
        </h1>
        <p className="text-zinc-400 text-lg max-w-3xl mx-auto">
          Master the art of gladiator management with proven strategies, optimization techniques, and competitive tips from top-ranking lanistas
        </p>
      </div>

      <div className="space-y-10 text-zinc-300">
        {/* Introduction */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded-lg p-8">
          <h2 className="font-cinzel text-3xl text-amber-300 mb-6 flex items-center gap-3">
            <Brain size={28} /> Strategic Foundations
          </h2>
          <div className="space-y-5 leading-relaxed">
            <p className="text-base">
              Success in Lanista Manager requires more than just recruiting gladiators and winning fights. Top lanistas understand that this game rewards long-term planning, resource optimization, and strategic decision-making across multiple interconnected systems. Whether you're aiming to dominate the PvP leaderboards, build the strongest roster, or maximize your gold income, mastering these advanced strategies will give you a competitive edge.
            </p>
            <p className="text-base">
              This comprehensive guide covers advanced tactics that go beyond the basics. We'll explore optimal training schedules, equipment forging strategies, tactical combat decision-making, beast breeding optimization, tournament preparation, and resource management techniques used by Diamond-tier players. Each section provides actionable advice you can immediately apply to improve your ludus performance.
            </p>
            <div className="bg-amber-950/20 border border-amber-700/40 rounded p-5 mt-6">
              <p className="text-amber-200 text-sm font-semibold mb-2">Pro Tip:</p>
              <p className="text-sm leading-relaxed">
                The most successful lanistas focus on mastering one system at a time rather than trying to optimize everything simultaneously. Start with training optimization and combat tactics, then gradually expand to equipment crafting, beast breeding, and alliance activities as you grow more experienced.
              </p>
            </div>
          </div>
        </section>

        {/* Training Optimization */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded-lg p-8">
          <h2 className="font-cinzel text-3xl text-amber-300 mb-6 flex items-center gap-3">
            <TrendingUp size={28} /> Training Schedule Optimization
          </h2>
          <div className="space-y-5">
            <p className="leading-relaxed">
              Training is the foundation of gladiator development. Three sessions run each day from Monday through Saturday (00:00, 08:00, and 16:00 server time); Sunday is forced rest. The key to optimal training lies in understanding diminishing returns, age curves, training speed, and potential.
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Understanding Stat Growth</h3>
                <p className="leading-relaxed mb-3">
                  Each gladiator has a hidden potential rating that determines when decline starts, while their separate training-speed value determines per-session gains. Age efficiency begins to taper after the earliest prime, and fully scouted potential determines the eventual decline threshold.
                </p>
                <ul className="space-y-2 ml-5 list-disc text-sm">
                  <li>Ages 20–20.2: Full age efficiency</li>
                  <li>After age 20.2: Age efficiency tapers gradually</li>
                  <li>Potential sets the slow- and rapid-decline ages</li>
                  <li>Elite fighters: slow decline at 28; rapid decline at 31</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Balanced vs Specialized Training</h3>
                <p className="leading-relaxed mb-3">
                  There are two primary training philosophies in Lanista: balanced development and focused specialization. Balanced gladiators train all six primary stats (Strength, Speed, Defense, Stamina, Precision, Technique) evenly, creating well-rounded fighters who can adapt to any opponent. Specialized gladiators focus heavily on 2-3 stats that align with their class strengths, becoming dominant in specific matchups.
                </p>
                <p className="leading-relaxed mb-3">
                  <strong className="text-amber-300">Balanced Training</strong> works best for: Beginners learning the game, gladiators you plan to keep long-term, fighters who will face many different opponents, and classes that benefit from versatility.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-amber-300">Specialized Training</strong> works best for: Tournament preparation against known opponents, creating counter-picks for specific enemy classes, maximizing burst damage potential, and late-career gladiators who need focused improvements before retirement.
                </p>
              </div>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Class-Specific Training Priorities</h3>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded p-4">
                    <h4 className="text-amber-400 font-semibold mb-2">Retiarius (Net & Trident)</h4>
                    <p className="text-sm mb-2">Primary: Speed, Precision</p>
                    <p className="text-sm mb-2">Secondary: Technique, Stamina</p>
                    <p className="text-sm text-zinc-400">High-mobility class that relies on landing critical net throws and quick strikes.</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded p-4">
                    <h4 className="text-amber-400 font-semibold mb-2">Dimachaerus (Dual Swords)</h4>
                    <p className="text-sm mb-2">Primary: Speed, Technique</p>
                    <p className="text-sm mb-2">Secondary: Strength, Precision</p>
                    <p className="text-sm text-zinc-400">Glass cannon that maximizes attack frequency and multi-hit potential.</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded p-4">
                    <h4 className="text-amber-400 font-semibold mb-2">Provocator (Sword & Shield)</h4>
                    <p className="text-sm mb-2">Primary: Defense, Technique</p>
                    <p className="text-sm mb-2">Secondary: Stamina, Precision</p>
                    <p className="text-sm text-zinc-400">Defensive counter-attacker that excels at absorbing attacks and punishing opponents' mistakes with calculated ripostes.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Combat Strategy */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded-lg p-8">
          <h2 className="font-cinzel text-3xl text-amber-300 mb-6 flex items-center gap-3">
            <Swords size={28} /> Combat Tactics & Matchup Analysis
          </h2>
          <div className="space-y-5">
            <p className="leading-relaxed">
              Combat in Lanista is an auto-simulation, but your strategic choices before the fight begins determine the outcome. Understanding the three tactical stances (Aggressive, Balanced, Defensive) and when to use each is crucial for maximizing your win rate.
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">The Three Tactical Stances</h3>
                <div className="space-y-4">
                  <div className="bg-red-950/20 border border-red-800/40 rounded p-4">
                    <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                      <Target size={18} /> Aggressive Stance
                    </h4>
                    <p className="text-sm mb-2 leading-relaxed">
                      Increases attack frequency and damage output by 15-20% but reduces defense and stamina efficiency by 10-15%. Your gladiator will take more risks, attempting more aggressive maneuvers and power attacks.
                    </p>
                    <p className="text-xs text-zinc-400">
                      <strong>Best Used When:</strong> You have a stat advantage, opponent is low on stamina, you need to end the fight quickly, or you're using a glass cannon build like Dimachaerus.
                    </p>
                  </div>

                  <div className="bg-blue-950/20 border border-blue-800/40 rounded p-4">
                    <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                      <Shield size={18} /> Defensive Stance
                    </h4>
                    <p className="text-sm mb-2 leading-relaxed">
                      Increases defense effectiveness and stamina conservation by 15-20% but reduces attack frequency and damage by 10-15%. Your gladiator will focus on blocks, parries, and counterattacks.
                    </p>
                    <p className="text-xs text-zinc-400">
                      <strong>Best Used When:</strong> You're at a stat disadvantage, opponent is aggressive, you have superior stamina, or you're using a tank build like Provocator.
                    </p>
                  </div>

                  <div className="bg-amber-950/20 border border-amber-800/40 rounded p-4">
                    <h4 className="text-amber-400 font-semibold mb-2 flex items-center gap-2">
                      <Zap size={18} /> Balanced Stance
                    </h4>
                    <p className="text-sm mb-2 leading-relaxed">
                      Provides no bonuses or penalties. Your gladiator fights according to their base stats without tactical modifiers. This is the safest choice when you're unsure of the opponent's approach.
                    </p>
                    <p className="text-xs text-zinc-400">
                      <strong>Best Used When:</strong> Stats are evenly matched, you lack information about opponent's strategy, you want to rely purely on your stat advantage, or for scrimmage testing.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Reading Your Opponent</h3>
                <p className="leading-relaxed mb-3">
                  Before challenging an opponent, check their gladiator profile to gather intelligence. Look for: their win/loss record (consistent winners likely have optimized builds), their class (suggests their stat priorities), their equipment quality (visible in their rating tier), and their recent fight results (reveals their preferred tactics).
                </p>
                <p className="leading-relaxed">
                  If their win streak is high, they're probably using Balanced or a counter-tactic to their matchups. If they have a mixed record, they might be experimenting with Aggressive plays. High-defense classes like Provocator typically favor Defensive stance, while glass cannons like Dimachaerus lean Aggressive.
                </p>
              </div>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Skill Tree Synergies</h3>
                <p className="leading-relaxed mb-3">
                  The tactical skill tree provides powerful modifiers that compound with your chosen stance. Unlocking offensive nodes (increased critical chance, armor penetration, multi-attack) synergizes with Aggressive stance, turning your gladiator into a devastating attacker. Defensive nodes (block chance, damage reduction, stamina recovery) amplify Defensive stance effectiveness, making your fighter nearly impenetrable.
                </p>
                <p className="leading-relaxed">
                  Strategic players unlock skill nodes that cover their weaknesses rather than doubling down on strengths. A high-offense Dimachaerus benefits more from defensive skill nodes to improve survivability than from additional offense nodes that provide diminishing returns.
                </p>
              </div>
            </div>

            <div className="bg-emerald-950/20 border border-emerald-700/40 rounded p-5 mt-6">
              <p className="text-emerald-200 text-sm font-semibold mb-2">Advanced Tactic:</p>
              <p className="text-sm leading-relaxed">
                Create a "tactics journal" tracking which stance you used against different opponents and the results. Over time, you'll identify patterns: certain classes always beat you with Aggressive, specific rating ranges require Defensive, etc. This data becomes your competitive advantage.
              </p>
            </div>
          </div>
        </section>

        {/* Resource Management */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded-lg p-8">
          <h2 className="font-cinzel text-3xl text-amber-300 mb-6 flex items-center gap-3">
            <Trophy size={28} /> Resource Management & Economy
          </h2>
          <div className="space-y-5">
            <p className="leading-relaxed">
              Efficient resource management separates good lanistas from great ones. Gold, fragments, skill points, and follies are all limited resources that require strategic allocation. Poor resource management leads to underdeveloped rosters and missed opportunities.
            </p>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Fragment Economics</h3>
                <p className="leading-relaxed mb-3">
                  Weapon and armor fragments are earned from victories and salvaged from unwanted equipment. Elite and Legendary gear requires hundreds of fragments to forge, so fragment management is critical for long-term progression. The most efficient fragment farming method is winning ranked PvP battles consistently, as each victory provides 5-15 fragments plus bonus fragments from win streaks.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-amber-300">Fragment Strategy:</strong> Salvage all Common and Uncommon equipment immediately — keeping low-tier gear wastes inventory space. Consider salvaging Rare gear unless it has perfect stat rolls for your needs. Only keep Elite and Legendary equipment with beneficial affixes. Stockpile fragments until you have enough to forge a full set of Elite gear rather than crafting piece by piece.
                </p>
              </div>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Skill Point Allocation</h3>
                <p className="leading-relaxed mb-3">
                  Skill points are earned through PvP victories, leveling up, and completing achievements. Each gladiator has their own skill tree with offensive, defensive, and utility branches. The most impactful early-game skill nodes are: increased critical damage (20-30% damage boost), enhanced block chance (15-20% defensive improvement), and stamina efficiency (fight lasts 2-3 more rounds).
                </p>
                <p className="leading-relaxed">
                  Don't spread skill points too thin across all gladiators. Focus on developing one or two "main" gladiators who will carry your roster in important battles. Once they're optimized, gradually improve your secondary fighters.
                </p>
              </div>
            </div>
        </section>

        {/* Competitive Play */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded-lg p-8">
          <h2 className="font-cinzel text-3xl text-amber-300 mb-6 flex items-center gap-3">
            <Users size={28} /> Competitive Play & Rankings
          </h2>
          <div className="space-y-5">
            <p className="leading-relaxed">
              Climbing the competitive ladder requires consistent performance, strategic matchmaking, and long-term roster development. Here's how top-tier players maintain their positions in Diamond and Platinum ranks.
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Rating System Mechanics</h3>
                <p className="leading-relaxed mb-3">
                  Your rating increases when you win and decreases when you lose, but the amount gained or lost depends on your opponent's rating relative to yours. Beating higher-rated opponents grants large rating gains (30-50 points) while losses cost less (10-20 points). Beating lower-rated opponents grants small gains (10-20 points) while losses are heavily punished (30-50 points).
                </p>
                <p className="leading-relaxed">
                  <strong className="text-amber-300">Smart Matchmaking:</strong> Challenge opponents 50-100 rating points above you for optimal risk/reward ratio. Avoid challenging opponents more than 150 points below you — the potential rating loss outweighs the small gain. Build win streaks against evenly-matched opponents for cumulative bonuses.
                </p>
              </div>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Tournament Preparation</h3>
                <p className="leading-relaxed mb-3">
                  Weekly tournaments use single-elimination brackets, meaning one loss ends your run. Successful tournament players prepare specific gladiators for bracket play by: ensuring all equipment is freshly repaired before signups close, analyzing common opponent classes in your tier, practicing tactics in scrimmages the day before, equipping your strongest beast, and having skill points saved for emergency tree unlocks.
                </p>
                <p className="leading-relaxed">
                  In tournament brackets, early rounds feature easier opponents, so use Balanced stance to conserve your gladiator's stamina. Semi-finals and finals require your best performance — switch to your strongest tactical counter based on opponent scouting.
                </p>
              </div>

              <div>
                <h3 className="text-xl text-amber-400 font-cinzel mb-3">Seasonal Strategy</h3>
                <p className="leading-relaxed">
                  Lanista operates on competitive seasons where rankings and rewards reset. Early season is the best time to climb ranks because: competition is less established, rating gains are more generous, tournaments have smaller fields, and you can claim crown holder status before veterans return. Mid-season focuses on optimization and consistency, while late-season emphasizes pushing for final rank rewards and preparing for the next season reset.
                </p>
              </div>
            </div>

            <div className="bg-purple-950/20 border border-purple-700/40 rounded p-5 mt-6">
              <p className="text-purple-200 text-sm font-semibold mb-2">Pro Player Mindset:</p>
              <p className="text-sm leading-relaxed">
                Top lanistas treat every ranked battle as valuable data. Win or lose, they review the combat log to understand what worked and what failed. They maintain spreadsheets tracking their win rates against different classes and rating ranges. This analytical approach transforms random battles into a systematic climb toward Legendary tier.
              </p>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <div className="bg-gradient-to-br from-amber-950/30 to-zinc-950 border border-amber-700/50 rounded-lg p-8 text-center">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4">Continue Your Mastery</h2>
          <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
            This advanced guide provides the foundation for competitive play. For specialized topics, explore our detailed guides below:
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <Link
              to="/guide"
              className="block p-4 bg-zinc-900/50 border border-zinc-800 hover:border-amber-700 rounded transition-colors"
            >
              <h3 className="text-amber-400 font-cinzel mb-2">Basic Guide</h3>
              <p className="text-sm text-zinc-400">Learn the fundamentals</p>
            </Link>
            <Link
              to="/faq"
              className="block p-4 bg-zinc-900/50 border border-zinc-800 hover:border-amber-700 rounded transition-colors"
            >
              <h3 className="text-amber-400 font-cinzel mb-2">FAQ</h3>
              <p className="text-sm text-zinc-400">Common questions answered</p>
            </Link>
            <Link
              to="/about"
              className="block p-4 bg-zinc-900/50 border border-zinc-800 hover:border-amber-700 rounded transition-colors"
            >
              <h3 className="text-amber-400 font-cinzel mb-2">About Lanista</h3>
              <p className="text-sm text-zinc-400">Game features & lore</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
